import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import SEO, { buildSiteSchema } from '../components/SEO';
import PropertyCard from '../components/PropertyCard';

const API = '/api';

export default function HomePage() {
  const [stats, setStats] = useState({ listings: null, users: null, newThisWeek: null });
  const [featured, setFeatured] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  useEffect(() => {
    fetch('/api/seo/stats')
      .then(r => r.json())
      .then(d => setStats(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/listings?sort=newest&page=1`)
      .then(r => r.json())
      .then(d => setFeatured((d.data || []).slice(0, 3)))
      .catch(() => {})
      .finally(() => setLoadingFeatured(false));
  }, []);

  function fmt(n) {
    if (n === null) return '...';
    if (n >= 1000) return n.toLocaleString('pl-PL');
    return n.toString();
  }

  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = () => {
    const p = new URLSearchParams();
    if (type !== 'all') p.set('type', type);
    if (query) p.set('city', query);
    navigate('/ogloszenia?' + p.toString());
  };

  return (
    <>
      <SEO
        title="Darmowy portal ogłoszeń nieruchomości — sprzedaż, wynajem"
        description="Nieruchomy.pl — darmowy portal ogłoszeń nieruchomości w Polsce. Mieszkania, domy, lokale. Sprzedaż i wynajem bez reklam i bez opłat."
        url="https://nieruchomy.pl"
        schema={buildSiteSchema()}
      />
      <div>
        <section className={styles.hero}>
          <div className={styles.heroBg} />
          <div className={styles.heroGrid} />
          <div className={styles.heroInner}>
            <div className={styles.eyebrow}>Portal nieruchomości</div>
            <h1>Znajdź miejsce,<br />które <em>stoi za tobą</em></h1>
            <p className={styles.heroSub}>
              Nieruchomy.pl — ogłoszenia sprawdzone, solidne i zakorzenione w polskim rynku nieruchomości
            </p>
            <div className={styles.searchBar}>
              <select value={type} onChange={e => setType(e.target.value)}>
                <option value="all">Wszystkie</option>
                <option value="sprzedaz">Sprzedaż</option>
                <option value="wynajem">Wynajem</option>
                <option value="nowe">Nowe</option>
              </select>
              <input
                type="text"
                placeholder="Wpisz miasto, dzielnicę lub adres…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
              <button className={styles.searchBtn} onClick={handleSearch}>Szukaj</button>
            </div>
          </div>
        </section>

        {(loadingFeatured || featured.length > 0) && (
          <section className={styles.featured}>
            <h2 className={styles.featuredHeading}>Wyróżnione ogłoszenia</h2>
            <p className={styles.featuredSub}>Wybrane przez nas oferty warte uwagi</p>
            {loadingFeatured && <div className={styles.status}>Ładowanie ofert...</div>}
            {!loadingFeatured && (
              <div className={styles.featuredGrid}>
                {featured.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}
              </div>
            )}
            <div className={styles.featuredCta}>
              <button className={styles.featuredCtaBtn} onClick={() => navigate('/ogloszenia')}>
                Zobacz wszystkie ogłoszenia →
              </button>
            </div>
          </section>
        )}

        <section className={styles.manifest}>
          <div className={styles.manifestInner}>
            <div className={styles.manifestEyebrow}>Nasza wizja</div>
            <h2 className={styles.manifestTitle}>W pełni darmowy.<br />Bez reklam. Na zawsze.</h2>
            <p className={styles.manifestText}>
              Nieruchomy.pl powstał z jednego przekonania — szukanie i wystawianie nieruchomości
              powinno być proste i bezpłatne. Bez ukrytych opłat, bez reklam, bez wyróżnień za
              dopłatą. Każde ogłoszenie traktujemy tak samo.
            </p>
            <div className={styles.manifestPillars}>
              {[
                { icon: '⓪', title: '0 zł za ogłoszenie', desc: 'Dodajesz bezpłatnie. Zawsze.' },
                { icon: '⊘', title: 'Zero reklam', desc: 'Żadnych banerów ani sponsorowanych wyników.' },
                { icon: '✓', title: 'Weryfikacja ogłoszeń', desc: 'Każde ogłoszenie przeglądamy ręcznie.' },
                { icon: '⚓', title: 'Zakorzenieni w Polsce', desc: 'Polski portal, polskie nieruchomości.' },
              ].map(p => (
                <div key={p.title} className={styles.pillar}>
                  <span className={styles.pillarIcon}>{p.icon}</span>
                  <span className={styles.pillarTitle}>{p.title}</span>
                  <span className={styles.pillarDesc}>{p.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.categories}>
          <h2 className={styles.catHeading}>Przeglądaj kategorie</h2>
          <p className={styles.catSub}>Znajdź dokładnie to, czego szukasz</p>
          <div className={styles.catGrid}>
            {[
              { label: 'Sprzedaż', desc: 'Mieszkania, domy i lokale na sprzedaż', icon: '🏢', type: 'sprzedaz', count: fmt(stats.byType?.sprzedaz ?? null) },
              { label: 'Wynajem', desc: 'Wynajem długoterminowy i krótkoterminowy', icon: '🔑', type: 'wynajem', count: fmt(stats.byType?.wynajem ?? null) },
              { label: 'Nowe budownictwo', desc: 'Prosto od deweloperów, stan deweloperski', icon: '🏗️', type: 'nowe', count: fmt(stats.byType?.nowe ?? null) },
            ].map(cat => (
              <button key={cat.type} className={styles.catCard} onClick={() => navigate('/ogloszenia?type=' + cat.type)}>
                <span className={styles.catIcon}>{cat.icon}</span>
                <span className={styles.catName}>{cat.label}</span>
                <span className={styles.catDesc}>{cat.desc}</span>
                <span className={styles.catCount}>{cat.count} ofert →</span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
